<template>
  <div
    class="tile__wrapper"
    :class="{ selected, excluded }"
    @click="$emit('click')"
  >
    <v-hover>
      <template #default="{ isHovering, props: hoverProps }">
        <v-card
          v-bind="hoverProps"
          class="tile"
          flat
        >
          <div
            v-ripple
            class="tile__inner"
          >
            <slot />

            <v-overlay
              contained
              :model-value="excluded"
              persistent
            />

            <v-overlay
              contained
              :model-value="isHovering"
              persistent
            />
          </div>

          <div
            v-if="selected"
            class="selected-icon"
          >
            <vi-icon :icon="selectedIcon" />
          </div>

          <div
            v-if="menuActions"
            v-ripple
            class="menu-icon"
          >
            <v-menu
              location="bottom end"
              origin="end top"
            >
              <template #activator="{ props: slotProps }">
                <icon-button
                  v-bind="slotProps"
                  :icon="menuIcon"
                  :ripple="false"
                  :size="32"
                  variant="plain"
                />
              </template>

              <v-list density="comfortable">
                <v-list-item v-if="menuTitle">
                  <span class="text-overline text-muted-light">
                    {{ menuTitle }}
                  </span>
                </v-list-item>

                <v-list-item
                  v-for="(config, action) in menuActions"
                  :key="action"
                  class="menu-item"
                  :title="config.text"
                  @click="$emit('click:action', { action })"
                >
                  <template
                    v-if="config.icon"
                    #prepend
                  >
                    <vi-icon :icon="config.icon" />
                  </template>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </v-card>

        <div class="caption text-muted-light">
          <slot name="caption" />
        </div>
      </template>
    </v-hover>
  </div>
</template>

<script setup lang="ts">
withDefaults(defineProps<{
  excluded?: boolean
  menuActions?: ButtonConfigMap
  menuIcon?: string
  menuTitle?: string
  selected?: boolean
  selectedIcon?: string
}>(), {
  menuIcon: 'mdi:dots-vertical',
  selectedIcon: 'mdi:star',
});
defineEmits(['click', 'click:action']);
</script>

<style scoped lang="scss">
@import "~/assets/styles/variables";

$tile-bg: white;
$tile-inner-bg: $ash-gray-100;
$tile-border: $ash-gray-200;
$selected-color: $poppy-red;

.tile__wrapper {
  cursor: pointer;
  position: relative;
  padding-bottom: 1.5rem;
  width: 100%;

  &.selected {
    .tile::after {
      border: 3px solid $selected-color;
    }
  }

  &.excluded {
    .caption {
      text-decoration: line-through;
    }

    .tile__inner {
      opacity: 0.25;
    }
  }
}

.caption {
  position: absolute;
  font-size: 0.75rem;
  line-height: 1;
  height: 1.25rem;
  padding-inline: 0.5rem;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  & > .v-icon {
    transform: translateY(0px);
  }
}

.tile {
  border-radius: 6px;
  position: relative;
  color: $muted-light;
  background-color: $tile-bg;
  padding-inline: 0.5rem;
  width: 100%;
  height: 0;
  padding-bottom: 100%;

  &::after {
    content: "";
    display: block;
    position: absolute;
    pointer-events: none;
    border-radius: inherit;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 1px solid $tile-border;
    z-index: 2;
  }

  .file-type-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

.tile__inner {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $tile-inner-bg;
  border-radius: 2px;
  overflow: hidden;
  position: absolute !important;
  top: 0.5rem;
  left: 0.5rem;
  right: 0.5rem;
  bottom: 0.5rem;
  z-index: 1;
}

.selected-icon,
.menu-icon {
  position: absolute;
  top: 0;
  z-index: 2;
  width: calc(32px + 3px);
  height: calc(32px + 3px);
}

.selected-icon {
  left: 3px;
  border-bottom-right-radius: 6px;
  background-color: $tile-bg;
  padding: 0.25rem;
  padding-top: calc(0.25rem + 3px);
  padding-left: calc(0.25rem + 3px);
  color: $selected-color;
}

.menu-icon {
  right: 0;
  border-bottom-left-radius: 6px;
  background-color: $tile-bg;
  padding-top: 3px;
  padding-right: 3px;
}

.menu-item {
  grid-template-columns: 2.5rem 1fr auto;
}
</style>
